import React, { useState, useEffect, useCallback } from 'react';
import { Newspaper, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';
import ManualLeitor from '../ManualLeitor';
import { COR, COR_ESCURA, COR_CLARA } from '../BlocosManual';
import mapearConecta from './mapearConecta';
import CuradoriaPortal from './CuradoriaPortal';

// ============================================================================
// BIBLIOTECA › CONECTA — a aba da newsletter
// ============================================================================
// A prateleira DINÂMICA do Conecta: a estante lê as edições publicadas do banco
// (GET /api/biblioteca/conecta) e, ao abrir uma, busca o STATE completo
// (GET /:chave), traduz para o formato do leitor (mapearConecta) e injeta no
// MESMO ManualLeitor dos manuais — só apontando o token para a rota do Conecta.
//
// ⚠️ SEM importação estática de conteúdo (o oposto dos Manuais): edição nova
//    aparece aqui SEM deploy, publicada pelo Gerador do Conecta (Marketing).
//
// ⚠️ A arte A4 do Gerador (capa âmbar, badges) fica DE FORA aqui de propósito
//    (decisão da DEM-00022): na Biblioteca o conteúdo é lido no padrão do módulo.
// ============================================================================

const FONTE = "'Raleway', sans-serif";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const estiloBtnGestao = {
  padding: '5px 10px', borderRadius: 6, border: '1px solid #e7e5e4',
  background: '#fafaf9', color: '#57534e', fontFamily: FONTE,
  fontSize: 12, fontWeight: 700, cursor: 'pointer',
};

// A gestão (tirar do ar / ordenar / excluir) é só do Admin. Lê o perfil do token
// guardado — o backend é a trava real (as rotas são authorizeRoles(['Admin'])).
function ehAdmin() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').perfil) === 'Admin'; }
  catch { return false; }
}

// Quem CURA o Manual do Portal: Marketing/Admin/Diretoria (a família). Espelha o
// PERFIS_CURA do backend, que é a trava real (authorizeRoles).
const PERFIS_CURA = ['Admin', 'Marketing', 'Diretoria', 'Diretoria Op'];
function podeCurar() {
  try { return PERFIS_CURA.includes(JSON.parse(localStorage.getItem('user') || '{}').perfil); }
  catch { return false; }
}

// Rótulo da prateleira quando a edição não tem módulo (MODULO nulo = geral).
const PRATELEIRA_GERAL = 'Conecta';

// Agrupa as edições por MODULO, preservando a ordem que o backend já devolveu
// (prateleiras nomeadas primeiro, geral por último; dentro, ordem/data).
function agruparPorModulo(edicoes) {
  const grupos = [];
  const indicePorNome = new Map();
  for (const ed of edicoes) {
    const nome = ed.MODULO || PRATELEIRA_GERAL;
    if (!indicePorNome.has(nome)) {
      indicePorNome.set(nome, grupos.length);
      grupos.push({ nome, edicoes: [] });
    }
    grupos[indicePorNome.get(nome)].edicoes.push(ed);
  }
  return grupos;
}

function formatarData(iso) {
  // A API devolve DATE como "AAAA-MM-DD" (ou ISO). Formata para "dd/mm/aaaa"
  // sem passar por `new Date` (que jogaria o fuso e poderia recuar um dia).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

const ConectaAba = ({ isMobile }) => {
  // estante: { estado: 'carregando'|'erro'|'pronto', edicoes: [] }
  const [estante, setEstante] = useState({ estado: 'carregando', edicoes: [] });
  // Edição aberta: { chave, manual } (manual já mapeado) ou null (na estante).
  const [aberta, setAberta] = useState(null);
  const [abrindo, setAbrindo] = useState(false);
  const [gestao, setGestao] = useState(false); // Admin: mostrar controles + fora do ar
  const [curando, setCurando] = useState(false); // curador: tela do Manual do Portal
  const admin = ehAdmin();
  const curador = podeCurar();

  // ---- carrega a estante ----
  // No modo gestão (só Admin), pede `?gestao=1` para vir também as fora do ar.
  const carregarEstante = useCallback(async (comGestao) => {
    setEstante({ estado: 'carregando', edicoes: [] });
    try {
      const url = comGestao ? '/api/biblioteca/conecta?gestao=1' : '/api/biblioteca/conecta';
      const r = await fetch(url, { headers: auth() });
      if (!r.ok) throw new Error(String(r.status));
      const edicoes = await r.json();
      setEstante({ estado: 'pronto', edicoes });
    } catch {
      setEstante({ estado: 'erro', edicoes: [] });
    }
  }, []);

  // ---- ações de gestão (só Admin) ----
  const alternarNoAr = async (chave, publicadoAtual) => {
    try {
      const r = await fetch(`/api/biblioteca/conecta/${encodeURIComponent(chave)}/publicado`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify({ publicado: publicadoAtual ? 0 : 1 }),
      });
      if (!r.ok) throw new Error(String(r.status));
      carregarEstante(true);
    } catch { /* silencioso — o estado não muda na tela */ }
  };
  const excluir = async (chave, titulo) => {
    // eslint-disable-next-line no-alert -- confirmação de ação irreversível
    if (!window.confirm(`Excluir a edição "${titulo}"? Isto apaga a edição e as imagens dela. Não dá para desfazer.`)) return;
    try {
      const r = await fetch(`/api/biblioteca/conecta/${encodeURIComponent(chave)}`, { method: 'DELETE', headers: auth() });
      if (!r.ok) throw new Error(String(r.status));
      carregarEstante(true);
    } catch { /* silencioso */ }
  };
  // Reordena DENTRO do grupo (a ORDEM só ordena dentro do mesmo módulo).
  const reordenar = async (chavesDoGrupo) => {
    try {
      await fetch('/api/biblioteca/conecta/ordem', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify({ chaves: chavesDoGrupo }),
      });
      carregarEstante(true);
    } catch { /* silencioso */ }
  };
  const moverNoGrupo = (edicoesGrupo, idx, dir) => {
    const chaves = edicoesGrupo.map((e) => e.CHAVE);
    const swap = idx + dir;
    if (swap < 0 || swap >= chaves.length) return;
    [chaves[idx], chaves[swap]] = [chaves[swap], chaves[idx]];
    reordenar(chaves);
  };

  const entrarGestao = () => { setGestao(true); carregarEstante(true); };
  const sairGestao = () => { setGestao(false); carregarEstante(false); };

  // ---- abre uma edição: busca o STATE, mapeia, injeta no leitor ----
  const abrir = useCallback(async (chave) => {
    setAbrindo(true);
    try {
      const r = await fetch(`/api/biblioteca/conecta/${encodeURIComponent(chave)}`, { headers: auth() });
      if (!r.ok) throw new Error(String(r.status));
      const dados = await r.json();
      setAberta({ chave, manual: mapearConecta(dados.conteudo, chave) });
      // Deep-link: `?aba=conecta&ed=conecta-000X`.
      const url = new URL(window.location.href);
      url.searchParams.set('ed', chave);
      window.history.replaceState(null, '', url);
      window.scrollTo({ top: 0 });
    } catch {
      // Falha ao abrir volta para a estante com um aviso leve (não trava a aba).
      setAberta(null);
    } finally {
      setAbrindo(false);
    }
  }, []);

  const voltar = useCallback(() => {
    setAberta(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('ed');
    url.searchParams.delete('cap'); // o capítulo é do leitor; não herdar para outra edição
    window.history.replaceState(null, '', url);
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => { carregarEstante(false); }, [carregarEstante]);

  // Deep-link de abertura: se a URL trouxe `?ed=`, abre assim que a estante
  // confirmar que a edição existe e está publicada.
  useEffect(() => {
    if (estante.estado !== 'pronto' || aberta) return;
    const ed = new URLSearchParams(window.location.search).get('ed');
    if (ed && estante.edicoes.some((e) => e.CHAVE === ed)) abrir(ed);
  }, [estante, aberta, abrir]);

  // -------------------------------------------------------------------
  if (aberta) {
    return (
      <ManualLeitor
        manual={aberta.manual}
        onVoltar={voltar}
        isMobile={isMobile}
        tokenUrl={`/api/biblioteca/conecta/${encodeURIComponent(aberta.chave)}/token`}
        rotuloVoltar="Todas as edições"
      />
    );
  }

  // Tela de curadoria do Manual do Portal (só curadores). Sub-tela cheia, como o
  // leitor: entra pelo botão e volta pela seta.
  if (curando) {
    return <CuradoriaPortal isMobile={isMobile} onSair={() => setCurando(false)} />;
  }

  return (
    <div>
      {/* ---- capa da estante ---- */}
      <div style={{
        background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
        borderRadius: 16, padding: isMobile ? 24 : 40, color: '#fff',
        marginBottom: 32, position: 'relative', overflow: 'hidden',
        boxShadow: `0 10px 15px -3px ${COR}4d`,
      }}>
        <div style={{ position: 'relative', zIndex: 10 }}>
          <h2 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 800, margin: '0 0 12px', color: '#fff' }}>
            Conecta
          </h2>
          <p style={{ color: '#fde9d3', fontSize: isMobile ? 15 : 18, maxWidth: 650, margin: 0, lineHeight: 1.6 }}>
            As edições da nossa newsletter interna: comunicados, procedimentos novos e as
            pessoas do Grupo Correia, reunidos por edição.
          </p>
        </div>
        <div style={{ position: 'absolute', right: -30, top: -40, opacity: 0.12 }}>
          <Newspaper size={250} color="#ffffff" />
        </div>
      </div>

      {curador && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: admin ? 12 : 20, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setCurando(true)}
            style={{
              padding: '8px 14px', borderRadius: 8, border: `1px solid ${COR}55`,
              background: COR_CLARA, color: COR_ESCURA,
              fontFamily: FONTE, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            🗂 Curar Manual do Portal
          </button>
          <span style={{ fontSize: 12.5, color: '#78716c' }}>
            Escolha, por comunicado, o módulo que entra no Manual do Portal (na aba Manuais).
          </span>
        </div>
      )}

      {admin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <button
            type="button"
            onClick={gestao ? sairGestao : entrarGestao}
            style={{
              padding: '8px 14px', borderRadius: 8, border: `1px solid ${COR}55`,
              background: gestao ? COR : COR_CLARA, color: gestao ? '#fff' : COR_ESCURA,
              fontFamily: FONTE, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {gestao ? '✓ Sair da gestão' : '⚙ Gerenciar edições'}
          </button>
          {gestao && (
            <span style={{ fontSize: 12.5, color: '#78716c' }}>
              Tirar do ar, reordenar e excluir. As edições fora do ar aparecem aqui, esmaecidas.
            </span>
          )}
        </div>
      )}

      {abrindo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: COR, marginBottom: 16 }}>
          <Loader2 size={16} className="animate-spin" /> Abrindo edição…
        </div>
      )}

      {estante.estado === 'carregando' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#78716c', padding: 24 }}>
          <Loader2 size={18} className="animate-spin" /> Carregando edições…
        </div>
      )}

      {estante.estado === 'erro' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: 16, borderRadius: 12,
          background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c',
        }}>
          <AlertTriangle size={18} />
          Não foi possível carregar as edições do Conecta.
          <button
            type="button"
            onClick={() => carregarEstante(gestao)}
            style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: '#b91c1c', fontWeight: 800, cursor: 'pointer' }}
          >
            Tentar de novo
          </button>
        </div>
      )}

      {estante.estado === 'pronto' && estante.edicoes.length === 0 && (
        <p style={{
          padding: '14px 18px', borderRadius: 12, background: '#fafaf9',
          border: '1px dashed #d6d3d1', fontSize: 13.5, lineHeight: 1.6, color: '#78716c',
        }}>
          Nenhuma edição publicada ainda. As edições do Conecta são publicadas pelo
          Gerador do Conecta, no módulo Marketing.
        </p>
      )}

      {estante.estado === 'pronto' && estante.edicoes.length > 0 && (
        agruparPorModulo(estante.edicoes).map((grupo) => (
          <section key={grupo.nome} style={{ marginBottom: 28 }}>
            <h3 style={{
              margin: '0 0 12px', fontFamily: FONTE, fontSize: 13, fontWeight: 800,
              color: '#a8a29e', textTransform: 'uppercase', letterSpacing: 0.7,
            }}>
              {grupo.nome}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 420px))',
              gap: 20,
            }}>
              {grupo.edicoes.map((ed, idx) => {
                const foraDoAr = gestao && ed.PUBLICADO === 0;
                return (
                  <div
                    key={ed.CHAVE}
                    style={{
                      background: '#fff', borderRadius: 16, padding: 22, border: '1px solid #e7e5e4',
                      display: 'flex', flexDirection: 'column', gap: 12, opacity: foraDoAr ? 0.55 : 1,
                    }}
                  >
                    {/* corpo clicável — abre a edição no leitor */}
                    <div
                      onClick={() => abrir(ed.CHAVE)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(ed.CHAVE); } }}
                      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 12 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 46, height: 46, borderRadius: 12, background: COR_CLARA,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <Newspaper size={23} style={{ color: COR }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {formatarData(ed.DATA_LANCAMENTO) && (
                              <span style={{ fontSize: 11, fontWeight: 800, color: '#a8a29e', textTransform: 'uppercase', letterSpacing: 0.7 }}>
                                {formatarData(ed.DATA_LANCAMENTO)}
                              </span>
                            )}
                            {foraDoAr && (
                              <span style={{ fontSize: 10, fontWeight: 800, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 999, padding: '1px 8px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                fora do ar
                              </span>
                            )}
                          </div>
                          <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 800, color: '#1c1917', lineHeight: 1.25 }}>
                            {ed.TITULO}
                          </h3>
                        </div>
                      </div>
                      {!gestao && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 7, paddingTop: 12,
                          borderTop: '1px solid #f5f5f4', color: COR, fontSize: 14, fontWeight: 800,
                        }}>
                          Abrir edição <ArrowRight size={15} />
                        </div>
                      )}
                    </div>

                    {/* controles de gestão (só Admin, em modo gestão) */}
                    {gestao && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 12, borderTop: '1px solid #f5f5f4' }}>
                        <button type="button" style={estiloBtnGestao} disabled={idx === 0} onClick={() => moverNoGrupo(grupo.edicoes, idx, -1)}>▲</button>
                        <button type="button" style={estiloBtnGestao} disabled={idx === grupo.edicoes.length - 1} onClick={() => moverNoGrupo(grupo.edicoes, idx, 1)}>▼</button>
                        <button type="button" style={estiloBtnGestao} onClick={() => alternarNoAr(ed.CHAVE, ed.PUBLICADO)}>
                          {ed.PUBLICADO ? 'Tirar do ar' : 'Voltar ao ar'}
                        </button>
                        <button type="button" style={{ ...estiloBtnGestao, color: '#b91c1c', borderColor: '#fca5a5' }} onClick={() => excluir(ed.CHAVE, ed.TITULO)}>
                          Excluir
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default ConectaAba;
